import React, { FunctionComponent, PropsWithChildren, useEffect, useState } from "react";
import { BrandService } from "@/src/BrandService";
import Head from "next/head";
import { useRouter } from "next/router";
import { IBreadCrumb } from "@/src/components/molecules/BreadCrumbs";
import { PublicSettings } from "@/src/config/PublicSettings";
import { SmartLink } from "../v2/SmartLink/SmartLink";
import { SiteHeader } from "@/src/components/organisms/SiteHeader";
import { SiteFooter } from "@/src/components/organisms/SiteFooter";

import SocialCard from "@/src/media/social/social-card.png";
import { Utils } from "@/src/Utils";
import { DataDog } from "@/src/scripts/DataDog";
import { useCanonicalURL } from "@/src/hooks";
import useBreakpoint from "@/src/hooks/useBreakpoint";
import { Segment } from "@/src/scripts/Segment";
import { WriteSonicSEOFixer } from "@/src/scripts/WriteSonicSEOFixer";
import { ISimpleContentPageFields } from "@/src/@types/contentful";
import { useAuth } from "@/src/hooks/useAuth";

// See https://evilmartians.com/chronicles/how-to-favicon-in-2021-six-files-that-fit-most-needs for fav-icon info
import favIcon from "@/src/media/favicon.ico";
import iconSVG from "@/src/media/icon.svg";
import applePNG from "@/src/media/apple-touch-icon.png";
import { LoginForm } from "../molecules/LoginForm";
import { TrackingUtils } from "@/src/utils/TrackingUtils";
import { useBuilderNavigation } from "@/src/hooks/useBuilderNavigation";
import { useDatadogViewName } from "@/src/hooks/useDatadogViewName";
import { usePollBuilderTestCookie } from "@/src/hooks/usePollBuilderTestCookie";
import clsx from "clsx";

const IS_PRODUCTION = PublicSettings.ENVIRONMENT === "production";
const DISABLE_SEARCH_ENGINE_INDEXING = PublicSettings.DISABLE_SEARCH_ENGINE_INDEXING;
const BRAND_DATA = BrandService.getCurrentBrandData();
const SOCIAL_CARD_DEFAULT_IMAGE = PublicSettings.CANONICAL_URL_ROOT + SocialCard.src;

export interface SiteContainerProps {
    accessLevel?: ISimpleContentPageFields["accessLevel"];
    displayHeaderCTA?: boolean;
    displayLogoOnly?: boolean;
    displayPhone?: boolean;
    employerPhoneNumber?: boolean;
    title?: string;
    slug?: string;
    description?: string;
    noIndex?: boolean;
    canonicalURL?: string;
    featuredImageURL?: string;
    breadcrumbs?: IBreadCrumb[];
    enableTracking?: boolean;
    isBuilderPage?: boolean;
    removeHreflang?: boolean;
    datadogReplaySampleRate?: number;
    dataDogViewName?: string;
    customCtaText?: string;
    customCtaLink?: string;
    customPhoneNumber?: string;
    mainWrapperClass?: string;
    /** Per-page override for BRAND_DATA.appendBrandToTitle. Set to false to use the title as-is, without the " | <brand>" suffix. */
    appendBrandToTitle?: boolean;
}

export const SiteContainer: FunctionComponent<PropsWithChildren<SiteContainerProps>> = props => {
    const {
        displayLogoOnly = false,
        displayPhone = true,
        accessLevel,
        isBuilderPage,
        datadogReplaySampleRate,
        employerPhoneNumber = false,
        dataDogViewName,
        customCtaText,
        customCtaLink,
        customPhoneNumber,
        mainWrapperClass
    } = props;
    const [isHydrated, setIsHydrated] = useState(false);
    let title = BRAND_DATA.name;
    const description = props.description;
    const calculatedCanonicalURL = useCanonicalURL();
    const finalCanonicalURL = props.canonicalURL ?? calculatedCanonicalURL;
    const socialCardImage = props.featuredImageURL ?? SOCIAL_CARD_DEFAULT_IMAGE;
    const router = useRouter();
    const { isAuthenticated, isAuthenticating, shouldVerify, setShouldVerify, setLastValidationTime } = useAuth({ accessLevel });
    const { breakpoint } = useBreakpoint();

    useEffect(() => {
        TrackingUtils.initializeTrackingParams();
    }, []);

    useBuilderNavigation({ isBuilderPage: !!isBuilderPage });

    if (props.title) {
        const appendBrandToTitle = props.appendBrandToTitle ?? BRAND_DATA.appendBrandToTitle;

        if (appendBrandToTitle === false) {
            title = props.title;
        } else {
            // Suffixed with the shorter title brand when set (Canada: "Spot Pet Insurance"); a title already ending with the full name keeps it
            title = Utils.withBrandSuffix(props.title, BRAND_DATA.titleBrandName ?? BRAND_DATA.name, [BRAND_DATA.name]);
        }
    }

    useDatadogViewName(dataDogViewName);

    usePollBuilderTestCookie({
        isEnabled: isBuilderPage && isHydrated,
        pollingDurationMs: 5000,
        pollingIntervalMs: 500,
        ddActionName: "Builder Test ID Added",
        debugMode: false
    });

    useEffect(() => {
        const hydrate = () => setIsHydrated(true);
        hydrate();
    }, []);

    function renderPreviewMode() {
        return (
            <div className="fixed inset-x-0 bottom-0 z-50 flex items-center bg-accent-primary px-3 py-1 text-accent-contrast">
                <div className="flex-1">Preview</div>
                <div className="text-xs">
                    <SmartLink href="/api/leave-preview">Leave</SmartLink>
                </div>
            </div>
        );
    }

    function renderAlternateLanguageSites() {
        if (props.removeHreflang) {
            return null;
        }

        const alternateLinks = BRAND_DATA.alternateLanguageSites
            ?.map((site, index) =>
                site.locales.map(locale => <link key={site.origin + index} rel="alternate" hrefLang={locale} href={Utils.getCurrentCanonicalURL(router, site.origin)} />)
            )
            .flat();

        return (
            <>
                <link rel="alternate" hrefLang="x-default" href={`${PublicSettings.HREFLANG_DEFAULT_URL}${Utils.getCurrentPath(router)}`} />
                {/* Self-referencing alternate: the page's own URL, like x-default above. Not finalCanonicalURL, which a
                    Contentful canonicalUrl override can point elsewhere and would make this page declare itself an
                    alternate of a different URL (ENG-19528). */}
                <link rel="alternate" hrefLang={router.locale} href={calculatedCanonicalURL} />
                {alternateLinks}
            </>
        );
    }

    const handleLoginSuccess = () => {
        if (!shouldVerify) {
            setShouldVerify(true);
        }
        setLastValidationTime(Date.now());
    };

    return (
        <>
            <Head>
                <title>{title}</title>
                <link rel="icon" href={favIcon.src} sizes="any" />
                <link rel="icon" href={iconSVG.src} type="image/svg+xml" />
                <link rel="apple-touch-icon" href={applePNG.src} />
                <link rel="manifest" href="/manifest.webmanifest" />
                <link rel="preconnect" href="https://images.ctfassets.net" />
                {props.description && <meta name="description" content={description} />}
                <link rel="canonical" href={finalCanonicalURL} />
                {renderAlternateLanguageSites()}
                {props.noIndex || !IS_PRODUCTION || DISABLE_SEARCH_ENGINE_INDEXING ? <meta name="robots" content="noindex" /> : null}
                <meta property="og:locale" content={router.locale} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={title} />
                {props.description && <meta property="og:description" content={description} />}
                <meta property="og:url" content={finalCanonicalURL} />
                <meta property="og:site_name" content={BRAND_DATA.name} />
                <meta property="og:image" content={socialCardImage} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta property="twitter:url" content={finalCanonicalURL} />
                <meta name="twitter:title" content={title} />
                {props.description && <meta name="twitter:description" content={description} />}
                <meta name="twitter:site" content={BRAND_DATA.twitterHandle} />
                <meta name="twitter:creator" content={BRAND_DATA.twitterHandle} />
                <meta name="twitter:image" content={socialCardImage} />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
            </Head>
            <div className={`SiteContainer flex min-h-screen flex-col`}>
                <SiteHeader
                    displayCTA={props.displayHeaderCTA}
                    displayLogoOnly={displayLogoOnly}
                    displayPhone={displayPhone}
                    employerPhoneNumber={employerPhoneNumber}
                    breadcrumbs={props.breadcrumbs}
                    isBuilderPage={!!isBuilderPage}
                    breakpoint={breakpoint}
                    customCtaText={customCtaText}
                    customCtaLink={customCtaLink}
                    customPhoneNumber={customPhoneNumber}
                />
                <main className={clsx("flex flex-1 flex-col gap-12 md:gap-18", mainWrapperClass)}>
                    {isAuthenticated ? props.children : <LoginForm accessLevel={accessLevel} onSuccess={handleLoginSuccess} isAuthenticating={isAuthenticating} />}
                </main>
                <SiteFooter customCtaLink={customCtaLink} />
                {router.isPreview ? renderPreviewMode() : null}
            </div>
            <Segment enableAdPixels={props.enableTracking} />
            <DataDog replaySampleRate={datadogReplaySampleRate} />
            <WriteSonicSEOFixer />
        </>
    );
};
